import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import { OptionIcon } from '@app/src/components/Form/Select'
import Avatar from '@app/src/components/Ui/Avatar'
import AllFiltersButton from '@app/src/pages/ResourceCollection/Filters/AllFiltersButton'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { RiskContext, RiskStatus, SustainabilityArea } from '@app/src/types/resourceExplorer'
import { getRiskStatusTranslationKey } from '@app/src/utils/riskStatusHelpers'
import { Box, Button, Chip, Grid } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { FiltersProps } from '..'
import { getESGColor, getESGIcon } from '../Collections/RiskScreening/ProviderRisksPageRow'
import FilterSelect from './FilterSelect'

type RiskScreeningProviderRisksFiltersProps = FiltersProps & {
  providerId: number
  riskStatusPreSelectedValues?: string[]
}

type FilterOptions = {
  label: string
  value: string | number | boolean
  adornment?: string
  additionalText?: string
}

const RiskScreeningProviderRisksFilters: React.FC<RiskScreeningProviderRisksFiltersProps> = ({
  allowedFilters,
  resetPage,
  providerId,
  riskStatusPreSelectedValues = [RiskStatus.Extreme, RiskStatus.High],
}) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()

  const riskStatusOptions: FilterOptions[] = Object.values(RiskStatus).map(value => ({
    label: value,
    value,
  }))

  const sustainabilityAreaOptions: FilterOptions[] = Object.values(SustainabilityArea).map(value => ({
    label: value,
    value,
  }))

  const riskContextOptions: FilterOptions[] = Object.values(RiskContext).map(value => ({
    label: value,
    value,
  }))

  return (
    <Grid container spacing={1}>
      <Filters allowedFilters={allowedFilters} resetPage={resetPage}>
        {({ filters, clearQueryFilter, inDrawerFiltersCount }) => {
          const isFiltered = Object.keys(filters).length > 1
          return (
            <>
              <Grid item md={3} sm={6}>
                <Filter operator={Operators.In} name="riskStatus">
                  {({ value, onChange, filterName }) => (
                    <FilterSelect
                      size="small"
                      fieldLabel={formatMessage({ id: 'schemas.risk.riskStatusFilter' })}
                      options={riskStatusOptions}
                      multiple
                      value={[value].flat()}
                      onChange={(newValue: string[], optionObjects) => onChange(newValue, true)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <OptionIcon selected={selected} multiple />
                          {formatMessage({ id: getRiskStatusTranslationKey(option.label as RiskStatus) })}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={formatMessage({ id: getRiskStatusTranslationKey(option.label as RiskStatus) })}
                            size="small"
                          />
                        ))
                      }
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={3} sm={6}>
                <Filter operator={Operators.In} name="riskSustainabilityArea">
                  {({ value, onChange, filterName }) => (
                    <FilterSelect
                      size="small"
                      multiple
                      options={sustainabilityAreaOptions}
                      fieldLabel={formatMessage({ id: 'riskScreening.providerRisksPage.headers.esgArea' })}
                      value={[value].flat()}
                      onChange={(newValue, optionObjects) => onChange(newValue)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <OptionIcon selected={selected} multiple />
                          <Avatar
                            sx={{
                              bgcolor: getESGColor(option.label),
                              width: 24,
                              height: 24,
                              mr: 1,
                            }}
                          >
                            {getESGIcon(option.label)}
                          </Avatar>
                          {formatMessage({
                            id: `schemas.risk.esgArea.${option.label}`,
                          })}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value?.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            avatar={
                              <Avatar sx={{ bgcolor: getESGColor(option.label) }}>{getESGIcon(option.label)}</Avatar>
                            }
                            label={formatMessage({
                              id: `schemas.risk.esgArea.${option.label}`,
                            })}
                            size="small"
                          />
                        ))
                      }
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={2} sm={6}>
                <Filter operator={Operators.In} name="riskContext">
                  {({ value, onChange, filterName }) => (
                    <FilterSelect
                      size="small"
                      multiple
                      options={riskContextOptions}
                      fieldLabel={formatMessage({ id: 'riskScreening.providerRisksPage.headers.context' })}
                      value={[value].flat()}
                      onChange={(newValue, optionObjects) => onChange(newValue)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <OptionIcon selected={selected} multiple />
                          {formatMessage({ id: `schemas.risk.context.${option.label}` })}
                        </li>
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={index}
                            label={formatMessage({ id: `schemas.risk.context.${option.label}` })}
                            size="small"
                          />
                        ))
                      }
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={2}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2}>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.riskScreening' })}>
                              <Filter name="riskStatus" operator={Operators.In}>
                                {({ filterName, value, onChange }) => (
                                  <FilterSelect
                                    fieldLabel={formatMessage({ id: 'schemas.risk.riskStatusFilter' })}
                                    options={riskStatusOptions}
                                    multiple
                                    value={[value].flat()}
                                    onChange={(newValue: string[], optionObjects) => onChange(newValue, true)}
                                    renderOption={(props, option, { selected }) => (
                                      <li {...props}>
                                        <OptionIcon selected={selected} multiple />
                                        {formatMessage({ id: getRiskStatusTranslationKey(option.label as RiskStatus) })}
                                      </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value.map((option, index) => (
                                        <Chip
                                          {...getTagProps({ index })}
                                          key={index}
                                          label={formatMessage({
                                            id: getRiskStatusTranslationKey(option.label as RiskStatus),
                                          })}
                                          size="small"
                                        />
                                      ))
                                    }
                                  />
                                )}
                              </Filter>
                              <Filter operator={Operators.In} name="riskSustainabilityArea">
                                {({ value, onChange, filterName }) => (
                                  <FilterSelect
                                    size="small"
                                    multiple
                                    options={sustainabilityAreaOptions}
                                    fieldLabel={formatMessage({
                                      id: 'riskScreening.providerRisksPage.headers.esgArea',
                                    })}
                                    value={[value].flat()}
                                    onChange={(newValue, optionObjects) => onChange(newValue)}
                                    renderOption={(props, option, { selected }) => (
                                      <li {...props}>
                                        <OptionIcon selected={selected} multiple />
                                        <Avatar
                                          sx={{
                                            bgcolor: getESGColor(option.label),
                                            width: 24,
                                            height: 24,
                                            mr: 1,
                                          }}
                                        >
                                          {getESGIcon(option.label)}
                                        </Avatar>
                                        {formatMessage({
                                          id: `schemas.risk.esgArea.${option.label}`,
                                        })}
                                      </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map((option, index) => (
                                        <Chip
                                          {...getTagProps({ index })}
                                          key={index}
                                          avatar={
                                            <Avatar sx={{ bgcolor: getESGColor(option.label) }}>
                                              {getESGIcon(option.label)}
                                            </Avatar>
                                          }
                                          label={formatMessage({
                                            id: `schemas.risk.esgArea.${option.label}`,
                                          })}
                                          size="small"
                                        />
                                      ))
                                    }
                                  />
                                )}
                              </Filter>
                              <Filter operator={Operators.In} name="riskContext">
                                {({ value, onChange, filterName }) => (
                                  <FilterSelect
                                    size="small"
                                    multiple
                                    options={riskContextOptions}
                                    fieldLabel={formatMessage({
                                      id: 'riskScreening.providerRisksPage.headers.context',
                                    })}
                                    value={[value].flat()}
                                    onChange={(newValue, optionObjects) => onChange(newValue)}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                          </Box>
                        </DrawerViewFilters>,
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button variant="text" onClick={clearQueryFilter} sx={{ ml: 1 }}>
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default RiskScreeningProviderRisksFilters
